import { useQuery } from '@tanstack/react-query';
import AllCorrectiveActionHub from '../../views/AssignedCorrectiveActions';
import { useSearchParams } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import {
  CorrectiveActionControllerGetAllCorrectiveActionOrganizationAssignEnum,
  CorrectiveActionControllerGetAllCorrectiveActionOrganizationPriorityEnum,
  CorrectiveActionControllerGetAllCorrectiveActionOrganizationStatusEnum,
  Department,
  GetAllDepartmentResponse,
  GetCorrectiveActionOrganizationResponse,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';

export default function AllCorrectiveActionHubPage() {
  const { CorrectiveActionApi, DepartmentApi } = useApiClients();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const currentPage = searchParams.get('page') ?? 1;
  const status = searchParams.get('status') ?? null;
  const priority = searchParams.get('priority') ?? null;
  const departmentId = searchParams.get('departmentId') ?? '';
  const assign = searchParams.get('assign') ?? null;
  const limit = Number(searchParams.get('limit')) || 10;

  const query: any = {
    page: Number(currentPage),
  };
  if (searchQuery) query.search = searchQuery;
  if (assign) query.assign = assign;
  if (status) query.status = status;
  if (priority) query.priority = priority;
  if (departmentId) query.departmentId = departmentId;
  if (limit) query.limit = Number(limit);
  const {
    data: allCorrectiveActionOrganization,
    isLoading,
    isRefetching,
  } = useQuery<GetCorrectiveActionOrganizationResponse, Error, any>({
    queryKey: [
      'all-corrective-action-organization',
      Number(currentPage),
      searchQuery,
      assign,
      priority,
      status,
      departmentId,
      Number(limit),
    ],
    queryFn: async () => {
      const response =
        await CorrectiveActionApi.correctiveActionControllerGetAllCorrectiveActionOrganization(
          {
            search: searchQuery,
            page: Number(currentPage) || 1,
            departmentId: departmentId,
            limit: Number(limit),
            assign:
              assign as CorrectiveActionControllerGetAllCorrectiveActionOrganizationAssignEnum,
            priority:
              priority as CorrectiveActionControllerGetAllCorrectiveActionOrganizationPriorityEnum,
            status:
              status as CorrectiveActionControllerGetAllCorrectiveActionOrganizationStatusEnum,
          },
        );

      return response?.data;
    },
  });

  const { data: allDepartments, isLoading: isDepartmentLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['departments'],
      queryFn: async () => {
        const response =
          await DepartmentApi.departmentControllerFindAllDepartment({
            disablePagination: true,
          });
        return response?.data;
      },
    });

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const hasFilters =
    !!searchQuery || !!status || !!priority || !!assign || !!departmentId;

  const noDataMessage = hasFilters
    ? 'No items match your filters or search. Try using different keywords maybe.'
    : 'No corrective action assigned';

  if (isDepartmentLoading) {
    return <Loader />;
  }

  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <AllCorrectiveActionHub
        data={
          allCorrectiveActionOrganization as GetCorrectiveActionOrganizationResponse
        }
        params={{
          search: searchQuery,
          page: Number(currentPage),
          assign:
            assign as CorrectiveActionControllerGetAllCorrectiveActionOrganizationAssignEnum,
          priority:
            priority as CorrectiveActionControllerGetAllCorrectiveActionOrganizationPriorityEnum,
          status:
            status as CorrectiveActionControllerGetAllCorrectiveActionOrganizationStatusEnum,
          departmentId: String(departmentId),
          limit: Number(limit),
        }}
        noDataMessage={noDataMessage}
        isIncidentsLoading={isLoading || isRefetching}
        departments={departments}
      />
    </div>
  );
}
